import { placeOptions } from "../../app-place-checklist/placeOptions";
import { townName } from "../../app-company-search/display";

/** Places are stored in the folded form the selectors submit; display needs the label. */
const LABEL_BY_VALUE = new Map(placeOptions.map((option) => [option.value, option.label]));

export const placeLabel = (value: string) => LABEL_BY_VALUE.get(value) ?? value;

export const placeLabels = (values?: string[] | null) =>
  (values && values.length ? values.map(placeLabel).join(", ") : "–");

type PlaceParts = { places: string[], performanceCities?: string[] };

/** The town of performance is more precise than the county, so it leads when TED gives one. */
export const placeLine = ({ places, performanceCities = [] }: PlaceParts) => {
  const towns = performanceCities.map(townName).join(", ");
  if (!towns) return placeLabels(places);
  return places.length ? `${towns} – ${placeLabels(places)}` : towns;
};

/** A nationwide framework carries every county; the panel has the full list. */
export const PLACES_SHOWN_IN_TABLE = 2;

export const placeSummary = (values?: string[] | null) => {
  if (!values || !values.length) return { text: "–", title: "" };
  const shown = values.slice(0, PLACES_SHOWN_IN_TABLE).map(placeLabel).join(", ");
  const rest = values.length - PLACES_SHOWN_IN_TABLE;
  return {
    text: rest > 0 ? `${shown} +${rest}` : shown,
    title: values.map(placeLabel).join(", "),
  };
};

/** TED's contract-nature codes. The raw value is English and must never reach the table. */
export const CONTRACT_NATURE_DEFAULTS: Record<string, string> = {
  works: "Byggentreprenad",
  services: "Tjänster",
  supplies: "Varor",
};

/** TED's procedure-type codes; an unlisted one falls back to the raw value, not an empty cell. */
export const PROCEDURE_TYPE_DEFAULTS: Record<string, string> = {
  open: "Öppet förfarande",
  restricted: "Selektivt förfarande",
  "neg-w-call": "Förhandlat med föregående annonsering",
  "neg-wo-call": "Förhandlat utan föregående annonsering",
  "comp-dial": "Konkurrenspräglad dialog",
  innovation: "Innovationspartnerskap",
  "oth-mult": "Annat, flera steg",
  "oth-single": "Annat, ett steg",
};

/** Mirrors SORTS in src/services/ted-service/noticeQuery.js; the spec here pins the pair. */
export const API_SORT_KEYS = ["deadline", "publicationDate", "title", "estimatedValue"] as const;

/** Column field -> API sort key. An unmapped column sends no sort, so it cannot 400. */
export const SORT_KEY_BY_FIELD: Record<string, typeof API_SORT_KEYS[number]> = {
  title: "title",
  publicationDate: "publicationDate",
  deadline: "deadline",
  estimatedValue: "estimatedValue",
};

/** A framework agreement can name a dozen winners; the panel has the full list. */
const WINNERS_SHOWN_IN_TABLE = 2;

/** Null for no names: whether nothing was awarded or the winner went unnamed is the caller's. */
export const winnerSummary = (winners: readonly { name: string }[]) => {
  if (!winners.length) return null;
  const names = winners.map((winner) => winner.name);
  const shown = names.slice(0, WINNERS_SHOWN_IN_TABLE).join(", ");
  const rest = names.length - WINNERS_SHOWN_IN_TABLE;
  return { text: rest > 0 ? `${shown} +${rest}` : shown, title: names.join(", ") };
};

/**
 * The Yrkeskategori cell, falling back to TED's own CPV term when nothing is mapped.
 * The `cpv` kind is marked so the UI can show it is not one of our categories - the
 * Kategori filter will not match it.
 */
export type CategoryCell =
  | { kind: "category"; text: string }
  | { kind: "cpv"; text: string }
  | { kind: "none"; text: "–" };

export const categoryCell = (
  category?: { name: string } | null,
  cpv?: { label?: string | null } | null,
): CategoryCell => {
  if (category?.name) return { kind: "category", text: category.name };
  if (cpv?.label) return { kind: "cpv", text: cpv.label };
  return { kind: "none", text: "–" };
};

export const formatValue = (amount?: number | null, currency?: string | null) => {
  if (amount === null || amount === undefined) return "–";
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: currency ? "currency" : "decimal",
      currency: currency || undefined,
      maximumFractionDigits: 0,
    })
      .formatToParts(amount)
      // Intl joins the currency with a no-break space; an ordinary one lets it wrap.
      .map((part) => (part.type === "literal" ? " " : part.value))
      .join("");
  } catch {
    // An unexpected currency code would otherwise throw and blank the whole table.
    return `${Math.round(amount).toLocaleString("sv-SE")} ${currency ?? ""}`.trim();
  }
};

export type NoticeAction = {
  key: "submission" | "documents" | "pdf" | "ted";
  href: string;
  primary: boolean;
};

/** Host and path only: the query string is where the same page gets two addresses. */
const samePage = (a: string, b: string): boolean => {
  try {
    const one = new URL(a);
    const two = new URL(b);
    return one.host.toLowerCase() === two.host.toLowerCase()
      && one.pathname.toLowerCase() === two.pathname.toLowerCase();
  } catch {
    return a === b;
  }
};

/**
 * The outbound actions, deduplicated. Across 250 Swedish notices the submission and
 * document addresses were the same page every time - identical on 63%, and on the rest
 * differing only by TendSign's &GoTo=Tender deep link. Comparing whole URLs left a third
 * of rows showing two buttons to one page.
 *
 * With no submission URL the documents link is promoted, being then the only route to
 * the buyer.
 */
export const noticeActions = (notice: {
  submissionUrl?: string | null;
  documentUrl?: string | null;
  noticeUrl: string;
  noticePdfUrl?: string | null;
  deadline?: string | null;
}, now: Date = new Date()): NoticeAction[] => {
  const actions: NoticeAction[] = [];
  const { noticeUrl, noticePdfUrl, deadline } = notice;
  const deadlinePassed = !!deadline && new Date(deadline) < now;
  const submissionUrl = deadlinePassed ? null : notice.submissionUrl;
  const documentUrl = notice.documentUrl || (deadlinePassed ? notice.submissionUrl : null);

  if (submissionUrl) actions.push({ key: "submission", href: submissionUrl, primary: true });
  if (documentUrl && !(submissionUrl && samePage(documentUrl, submissionUrl))) {
    actions.push({ key: "documents", href: documentUrl, primary: !submissionUrl });
  }
  if (noticePdfUrl) actions.push({ key: "pdf", href: noticePdfUrl, primary: false });
  actions.push({ key: "ted", href: noticeUrl, primary: false });
  return actions;
};


/**
 * The CPV codes a set of yrkeskategorier maps to, deduplicated and in a stable order.
 * Picking a category fills them into the CPV control rather than resolving the mapping
 * invisibly on the server: the codes a category stands for are the useful part.
 */
export const cpvCodesForCategories = (
  categoryIds: readonly string[],
  categories: readonly { id: string, cpvCodes: string[] }[],
): string[] => {
  const byId = new Map(categories.map((category) => [category.id, category.cpvCodes]));
  const codes = new Set<string>();
  for (const id of categoryIds) (byId.get(id) || []).forEach((code) => codes.add(code));
  // Array.from, not a spread: the es5 target cannot iterate a Set.
  return Array.from(codes).sort();
};

/**
 * Which of the chosen categories the CPV selection still stands behind. Removing a code a
 * category brought in says that category no longer describes what the user wants, so it is
 * dropped rather than left to silently re-add the code on the next save.
 *
 * A category that maps to no codes is kept: it constrains nothing to prune.
 */
export const categoriesStillCovered = (
  categoryIds: readonly string[],
  categories: readonly { id: string, cpvCodes: string[] }[],
  selectedCodes: readonly string[],
): string[] => {
  const selected = new Set(selectedCodes);
  return categoryIds.filter(
    (id) => cpvCodesForCategories([id], categories).every((code) => selected.has(code)),
  );
};




/**
 * The deadline as the buyer wrote it. TED publishes a calendar day in the buyer's offset,
 * so rendering the stored instant shows the next day to a reader east of them: a Swedish
 * 8 October deadline reads as 9 oktober in Tokyo. Formatting the date parts directly keeps
 * the day the buyer published, wherever it is read.
 */
export const deadlineDayParts = (notice: { deadlineLocalDate?: string | null, deadline?: string | null }) => {
  const local = notice.deadlineLocalDate;
  if (local && /^\d{4}-\d{2}-\d{2}$/.test(local)) {
    const [year, month, day] = local.split("-").map(Number);
    return { year, month: month - 1, day };
  }
  if (!notice.deadline) return null;
  const parsed = new Date(notice.deadline);
  if (Number.isNaN(parsed.getTime())) return null;
  return { year: parsed.getFullYear(), month: parsed.getMonth(), day: parsed.getDate() };
};
